import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text'
import FlexContainer from 'core-system/FlexContainer'
import RadialChartCenterText, {
  colors,
} from 'core-system/charts/Radial/RadialChartCenterText'
import { GrhUsageHistory } from 'redux/grh/grhTypes'
import pxToRem from 'core-system/utils/pxToRem'
import Loading from 'core-system/Loading'
import palette from 'core-system/Themes/palette'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import LegendColor from 'core-system/Legend'

const Container = styled.div`
  ${(props) => props.theme.baseCard}
  overflow: hidden;
  display: flex;
  flex-direction: column;
`
const getChartData = (usageData: GrhUsageHistory[]) => {
  return usageData.reduce((reasons, transaction) => {
    const formattedReason = transaction.reason.startsWith('Other')
      ? 'Other'
      : transaction.reason
    reasons[formattedReason] = reasons[formattedReason] || 0
    reasons[formattedReason] += 1
    return reasons
  }, {})
}

interface GRHReasonsBreakdownCardProps {
  usageHistory: GrhUsageHistory[]
}

const GRHReasonsBreakdownCard = React.memo(
  (props: GRHReasonsBreakdownCardProps) => {
    const { usageHistory } = props
    const merchantsMap = useSelector(
      (state: AppState) => state.programs.merchantsMap
    )

    if (!usageHistory || !merchantsMap) return <Loading height={pxToRem(417)} />

    const groupedReasons = getChartData(usageHistory)

    return (
      <Container>
        <Text variant='h5' marginBottom='1.5rem'>
          Reasons Breakdown
        </Text>
        <FlexContainer
          flexDirection='column'
          justifyContent='space-between'
          flex={1}
        >
          <FlexContainer center width='100%' marginBottom={'1.5rem'}>
            <RadialChartCenterText
              value={usageHistory.length}
              label={usageHistory.length === 1 ? 'Reason' : 'Reasons'}
              data={Object.values(groupedReasons)}
              labels={Object.keys(groupedReasons)}
              size={195}
              errorMsg='No transactions yet'
            />
          </FlexContainer>
          {usageHistory.length === 0 ? (
            <FlexContainer center>
              <Text variant='body1'>No transactions yet</Text>
            </FlexContainer>
          ) : (
            Object.keys(groupedReasons).map((reason, idx) => (
              <FlexContainer key={idx} alignItems='center'>
                <LegendColor color={colors[idx]} marginRight='0.5rem' />
                <Text variant='action4'>{reason}</Text>
                <Text
                  variant='body2'
                  marginLeft='auto'
                  textColor={palette.text.secondary}
                >
                  {groupedReasons[reason]}{' '}
                  {groupedReasons[reason] === 1 ? 'Trip' : 'Trips'}
                </Text>
              </FlexContainer>
            ))
          )}
        </FlexContainer>
      </Container>
    )
  }
)

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  GRHReasonsBreakdownCard.displayName = 'GRHReasonsBreakdownCard'
}

export default GRHReasonsBreakdownCard
